import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Flex } from 'reflexbox/styled-components'
import omit from 'lodash/omit'

import baseTheme from 'theme'

import ArrowLink from 'components/ArrowLink'

import { parseURL, getQueryString } from 'helpers'
import { sortPosts } from 'lib/content/helpers'

const SEARCH_QUERY_PARAM = 'q'

const NoResultsContainer = styled(Flex).attrs({
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
})`
  min-height: 300px;
  height: 100%;
`

const Message = styled.div`
  text-align: center;
`

const FlexContainer = styled(Flex).attrs({
  flexDirection: ['column', 'row'],
  justifyContent: 'space-between',
  alignItems: 'center',
  mt: 50,
  width: [450],
})`
  a:hover {
    color: ${({ theme }) =>
      theme?.textColors?.linkHover || baseTheme.textColors.linkHover};
  }
`

const Container = styled.div`
  margin-top: 50px;

  a:hover {
    color: ${({ theme }) =>
      theme?.textColors?.linkHover || baseTheme.textColors.linkHover};
  }
`

const sanitizeHash = (hash) => (hash.includes('?') ? hash.split('?')[0] : hash)

const getBasePath = () => {
  return `${document.location.pathname}${sanitizeHash(document.location.hash)}`
}

const getQueryParams = () => {
  const { query } = parseURL(document.location.href)
  return query
}

const getResults = ({
  posts,
  results,
  searchOptions: { idsOnly, idFieldName = 'id' } = { idsOnly: false },
}) => {
  if (idsOnly) {
    return posts.filter((post) => results.includes(post[idFieldName]))
  }

  return results
}

function useSearch ({ pageSlug, posts, pageNumber, tag }) {
  const [query, setQuery] = useState('') // the search query
  const [results, setResults] = useState([])

  const ORIGINAL_POSTS = posts

  useEffect(() => {
    const basePath = getBasePath()
    const { query: parsedQuery } = parseURL(document?.location?.href) || {}
    const curQuery = parsedQuery?.[SEARCH_QUERY_PARAM]

    setResults(posts)

    if (curQuery && curQuery.length > 0) {
      setQuery(curQuery)
      const queryString = getQueryString({
        ...getQueryParams(),
        [SEARCH_QUERY_PARAM]: curQuery,
      })
      window.history.replaceState(null, '', `${basePath}${queryString}`)
    }
  }, [])

  const handleSearch = ({ query, results, searchOptions }) => {
    const basePath = getBasePath()

    let queryString
    if (query) {
      queryString = getQueryString({
        ...getQueryParams(),
        [SEARCH_QUERY_PARAM]: query,
      })
    } else {
      const queryObject = omit(getQueryParams(), [SEARCH_QUERY_PARAM])
      queryString = getQueryString(queryObject)
    }

    window.history.replaceState(null, '', `${basePath}${queryString}`)

    const shouldUseAllPosts =
      !query || !results || (query === '' && results.length === 0)

    const newResults = shouldUseAllPosts
      ? ORIGINAL_POSTS
      : getResults({ posts: ORIGINAL_POSTS, results, searchOptions })

    setQuery(query)
    setResults(newResults.sort(sortPosts))
  }

  const handleClearSearch = () => {
    setResults(ORIGINAL_POSTS)
    setQuery('')

    const basePath = getBasePath({ pageSlug, tag, pageNumber })
    const queryObject = omit(getQueryParams(), [SEARCH_QUERY_PARAM])
    window.history.replaceState(
      null,
      '',
      `${basePath}${getQueryString(queryObject)}`
    )
  }

  // TODO: probably should move secondLink to be a parameter of this func instead...
  const renderNoResults = ({
    topElements = <></>,
    message = (
      <>
        We can't find any articles
        <br />
        matching your search terms.
      </>
    ),
    linkText = 'Browse all articles',
    secondLink,
  }) => {
    const LinkContainer = secondLink ? FlexContainer : Container

    return (
      <>
        {topElements}
        <NoResultsContainer>
          <Message>{message}</Message>
          <LinkContainer>
            <ArrowLink
              to={pageSlug ? `/${pageSlug}/` : undefined}
              onClick={handleClearSearch}
            >
              {linkText}
            </ArrowLink>
            {secondLink && (
              <ArrowLink to={secondLink?.to} onClick={secondLink?.onClick}>
                {secondLink.text}
              </ArrowLink>
            )}
          </LinkContainer>
        </NoResultsContainer>
      </>
    )
  }

  return {
    query,
    results,
    onSearch: handleSearch,
    onClearSearch: handleClearSearch,
    renderNoResults,
  }
}

export const searchPropsShape = {
  query: PropTypes.string,
  results: PropTypes.array,
  onSearch: PropTypes.func,
  onClearSearch: PropTypes.func,
  renderNoResults: PropTypes.func,
}

export default useSearch
